/**
 * galleryImages — the gallery tag vocabulary, and the pure selection pass that
 * turns an extractor's `taggedImages` into the gallery shots an import should
 * upload.
 *
 * The vocabulary is the admin's, not the extractor's: an extractor is free to
 * invent a tag, but only a tag the ref editor can render as a chip (and the
 * enrich prompt knows how to route) is worth storing. Unknown tags are dropped
 * with a warning rather than stored, so a typo upstream shows up in the console
 * instead of as a chip nobody can toggle off.
 *
 * Deliberately free of Firebase imports — the upload/write side lives in
 * BulkImport, this half is unit-testable on its own (same arrangement as
 * enrichImageCollection.ts).
 */

import type { ImageInfo, ImageTag, TaggedImage } from '@watchkeeper/shared-types';

/**
 * Tags a gallery image may carry. Mirrors shared-types' `ImageTag` union — the
 * `satisfies` below is what makes a drift between the two a compile error
 * rather than a silently un-toggleable chip.
 */
export const GALLERY_IMAGE_TAGS = [
  'dial',
  'date',
  'caseback',
  'crown',
  'movement',
  'bracelet',
  'clasp',
  'bezel',
  'lume',
  'strap',
  'side',
  'wrist',
  'box',
  'papers',
] as const satisfies readonly ImageTag[];

export type GalleryImageTag = (typeof GALLERY_IMAGE_TAGS)[number];

const TAG_SET: ReadonlySet<string> = new Set(GALLERY_IMAGE_TAGS);

export function isGalleryImageTag(tag: string): tag is GalleryImageTag {
  return TAG_SET.has(tag);
}

/**
 * Lower-cases, trims and dedups tags, dropping anything outside the vocabulary.
 *
 * `onDropped` is called once per unknown tag so the caller can warn with its own
 * context (which reference, which URL) — this module has none.
 */
export function sanitizeImageTags(
  tags: readonly string[] | undefined,
  onDropped?: (tag: string) => void,
): GalleryImageTag[] {
  const out: GalleryImageTag[] = [];
  for (const raw of tags ?? []) {
    const tag = String(raw ?? '').trim().toLowerCase();
    if (!tag) continue;
    if (!isGalleryImageTag(tag)) {
      onDropped?.(tag);
      continue;
    }
    if (!out.includes(tag)) out.push(tag);
  }
  return out;
}

/**
 * Gallery images the ref editor holds beyond the hero shot. Matches the editor's
 * own cap, so an import can never leave a curator with more images than the UI
 * lets them manage.
 */
export const MAX_GALLERY_IMAGES = 5;

/** One image an import should fetch and upload into the gallery. */
export interface GalleryImageCandidate {
  url: string;
  tags: GalleryImageTag[];
}

/**
 * The tags the extractor inferred for the HERO shot — the one the importer
 * uploads as `images[0]` rather than into the gallery.
 *
 * Cartier, for one, tags only the hero (`dial`) and leaves the rest of the
 * carousel untagged, so without this the single most confident tag in the whole
 * scrape would be the one thrown away. Note the ref editor renders tag chips for
 * gallery images only, so these are stored, not shown.
 */
export function selectHeroImageTags(
  taggedImages: readonly TaggedImage[] | undefined,
  primaryUrl: string | undefined,
  onDroppedTag?: (tag: string, url: string) => void,
): GalleryImageTag[] {
  const primary = (primaryUrl ?? '').trim();
  if (!primary) return [];
  const key = dedupKey(primary);
  const hero = (taggedImages ?? []).find(
    (img) => dedupKey(String(img?.url ?? '').trim()) === key,
  );
  if (!hero) return [];
  return sanitizeImageTags(hero.tags, (tag) => onDroppedTag?.(tag, primary));
}

/**
 * Identity for dedup: host + path, case-folded, with the query string and
 * fragment dropped.
 *
 * The query is where CDNs put resize/format parameters (`?w=1400`,
 * `/upload/w_800/…?_a=…`), so the hero shot regularly reaches us as one URL in
 * `imgSrc` and the same asset at a different width in `taggedImages` — a raw
 * string compare would upload it twice and hand the curator a duplicate. Two
 * genuinely different assets differ in the path, so nothing distinct collapses.
 */
function dedupKey(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}`.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

/**
 * Priority tiers for the gallery's 5 slots, most useful first.
 *
 * A source can emit far more shots than the gallery holds — Cartier's JSON-LD
 * carousel runs to ten, tagged `dial` at index 0, `box` at 8–9 and nothing in
 * between — so taking the first five in page order fills the gallery with
 * unidentified shots and drops every tagged one. Tags are the whole point of
 * this path, so a tagged shot outranks an untagged one even when the tag is
 * `box`: the curator can see what they got, and the enrich call's own selector
 * (functions/src/enrichImageSelection.ts) re-ranks at send time anyway.
 *
 * That last part is the deliberate difference from the server-side tiers, which
 * rank untagged ALONGSIDE box/papers/wrist. There the question is "which of the
 * images we hold is worth paying for"; here it is "which do we keep at all", and
 * a discarded image is gone until someone re-imports.
 *
 * `case` is absent because it isn't in {@link GALLERY_IMAGE_TAGS} — sanitize
 * would have dropped it before tiering. `side` covers the same shot.
 */
export const GALLERY_TAG_TIERS: ReadonlyArray<readonly GalleryImageTag[]> = [
  ['dial'],
  ['date', 'caseback', 'bezel', 'crown', 'movement', 'side'],
  ['bracelet', 'strap', 'clasp', 'lume'],
  ['box', 'papers', 'wrist'],
];

/** Tier for an image whose tags matched none of the tiers — i.e. untagged. */
const UNTAGGED_TIER = GALLERY_TAG_TIERS.length;

/** The best (lowest) tier any of an image's tags earns it. */
export function galleryTierForTags(tags: readonly string[]): number {
  const best = UNTAGGED_TIER;
  for (let i = 0; i < GALLERY_TAG_TIERS.length; i++) {
    if (tags.some((t) => (GALLERY_TAG_TIERS[i] as readonly string[]).includes(t))) {
      return Math.min(best, i);
    }
  }
  return best;
}

/**
 * Every gallery-worthy shot for one entry, MOST USEFUL FIRST and uncapped —
 * {@link planGalleryMerge} applies the cap, because how many slots are free
 * depends on what the reference already holds.
 *
 * Drops:
 *   - the hero image, which the importer uploads separately into `images[0]`
 *     and which the extractor contract puts first in `taggedImages`;
 *   - anything that isn't an absolute http(s) URL (a data: or relative src can't
 *     be re-fetched);
 *   - repeats of a URL already selected.
 *
 * Untagged images are KEPT: the enrich prompt treats an untagged shot as
 * supporting evidence, and a curator can tag it by hand afterwards. An image is
 * worth having whether or not the extractor could say what it shows — it just
 * queues behind the ones that identified themselves.
 */
export function selectGalleryImageCandidates(
  taggedImages: readonly TaggedImage[] | undefined,
  primaryUrl: string | undefined,
  onDroppedTag?: (tag: string, url: string) => void,
): GalleryImageCandidate[] {
  const seen = new Set<string>();
  const primary = (primaryUrl ?? '').trim();
  if (primary) seen.add(dedupKey(primary));

  const ranked: Array<{ candidate: GalleryImageCandidate; tier: number; order: number }> = [];
  for (const img of taggedImages ?? []) {
    const url = String(img?.url ?? '').trim();
    if (!/^https?:\/\//i.test(url)) continue;
    const key = dedupKey(url);
    if (seen.has(key)) continue;
    seen.add(key);
    const tags = sanitizeImageTags(img?.tags, (tag) => onDroppedTag?.(tag, url));
    ranked.push({
      candidate: { url, tags },
      tier: galleryTierForTags(tags),
      order: ranked.length,
    });
  }

  // Stable: page order decides ties, so same-tier shots keep the source's own
  // idea of which matters more.
  ranked.sort((a, b) => a.tier - b.tier || a.order - b.order);
  return ranked.map((r) => r.candidate);
}

/** The two image arrays a promote rewrites, ready to hand to Firestore. */
export interface PromoteGalleryImageResult {
  images: ImageInfo[];
  galleryImages: ImageInfo[];
}

/**
 * Rebuild an image object carrying only the keys that have a value — the shape
 * to hand Firestore whenever an `images`/`galleryImages` array is rewritten.
 *
 * `updateWatchReference`'s `stripUndefined` is SHALLOW and the client Firestore
 * isn't initialised with `ignoreUndefinedProperties`, so an `undefined` sitting
 * inside an array element reaches the SDK and throws. Spreading the original
 * image (`{ ...img, sourceUrl: trimmed || undefined }`) carries exactly that
 * risk, hence the explicit rebuild.
 *
 * OMITTING the key is how a value is cleared here, in preference to writing
 * `null`. Both these arrays are rewritten whole on every edit, so an absent key
 * IS the delete — no `deleteField()` sentinel, whose array-element semantics
 * would be the shaky part. It also keeps the doc honest against shared-types,
 * where `sourceUrl?: string` has no null in it: every reader treats missing and
 * empty alike, whereas a null would survive a `typeof === 'string'` check in RN
 * and render as text.
 *
 * `keepTags: false` additionally drops tags — for an image being promoted to
 * `images[0]`, where the gallery vocabulary no longer applies.
 */
export function imageForWrite(
  img: ImageInfo,
  opts: { index?: number; keepTags?: boolean } = {},
): ImageInfo {
  const index = opts.index ?? img.index;
  const out: ImageInfo = {
    // Legacy docs predate parts of this shape; `?? ''` keeps a missing value
    // from becoming the undefined this function exists to prevent.
    storagePath: img.storagePath ?? '',
    contentType: img.contentType ?? '',
    index: Number.isFinite(index) ? index : 0,
  };
  if (opts.keepTags !== false && img.tags && img.tags.length > 0) {
    out.tags = [...img.tags];
  }
  const sourceUrl = img.sourceUrl?.trim();
  if (sourceUrl) out.sourceUrl = sourceUrl;
  return out;
}

/**
 * Swap a gallery image with the main (hero) image.
 *
 * Extractors regularly pick a wristshot or a lifestyle crop as the hero while a
 * clean packshot sits in the gallery, and `images[0]` is what AI recognition and
 * the RN detail view both read — so a curator needs to be able to correct it
 * without re-uploading bytes. Nothing moves in Storage: both arrays hold
 * `storagePath` references, so this is purely a reordering of pointers.
 *
 * The rules:
 *   - the promoted image becomes `images[0]` with its TAGS DROPPED. Gallery tags
 *     describe a detail shot (`dial`, `caseback`, `wrist`); a hero is the whole
 *     watch, so they'd be a lie once it's the main image. Nothing downstream
 *     loses out — the enrich collector overwrites the primary's tags with a
 *     synthetic `primary` anyway (see enrichImageCollection.ts).
 *   - the old `images[0]` drops into the slot the promoted image just vacated,
 *     KEEPING its tags. Same reasoning inverted: a wristshot's `wrist` tag is
 *     accurate again once it's a gallery shot. Reusing the vacated slot rather
 *     than appending keeps the curator's gallery order stable.
 *   - `sourceUrl` survives in both directions — it's attribution for the asset,
 *     not a statement about which slot it sits in.
 *   - with no main image to swap back, the gallery simply loses that entry.
 *   - `images[1..]` are left untouched. No admin path writes them today, but
 *     dropping them silently would be the wrong way to find that out.
 *
 * Returns null when `index` names no gallery image, so the caller can no-op
 * instead of writing a corrupted pair of arrays.
 */
export function promoteGalleryImageToMain(
  images: readonly ImageInfo[] | undefined,
  gallery: readonly ImageInfo[] | undefined,
  index: number,
): PromoteGalleryImageResult | null {
  const held = gallery ?? [];
  const promoted = held[index];
  if (!promoted) return null;

  const currentImages = images ?? [];
  const oldMain = currentImages[0];

  const newMain = imageForWrite(promoted, { index: 0, keepTags: false });
  const nextImages = [newMain, ...currentImages.slice(1)];

  // Only the touched entries are re-stamped; the rest keep whatever `index`
  // they already carry, exactly as removing a gallery image does.
  const nextGallery = oldMain
    ? held.map((img, i) => (i === index ? imageForWrite(oldMain, { index }) : img))
    : held.filter((_, i) => i !== index);

  return { images: nextImages, galleryImages: [...nextGallery] };
}

/** A tag backfill for a gallery image the reference already holds. */
export interface GalleryTagUpdate {
  /** Position in the EXISTING `galleryImages` array. */
  index: number;
  tags: GalleryImageTag[];
}

export interface GalleryMergePlan {
  tagUpdates: GalleryTagUpdate[];
  /** Candidates the reference doesn't hold yet, capped to the free slots. */
  toAdd: GalleryImageCandidate[];
}

/**
 * What a re-import should do to a reference's existing gallery.
 *
 * Tag backfill is ADDITIVE and runs on every import, overwrite toggle or not —
 * tags are metadata about an image the reference already has, not a value the
 * scrape is overwriting, and the alternative (tags only on first create) would
 * mean deleting and re-importing hundreds of live references to get them.
 *
 * The rules, in one place:
 *   - an existing image with NO tags gets the extractor's, matched by source URL;
 *   - an existing image that already HAS tags is left alone — a curator's edit
 *     beats an extractor's guess, in overwrite mode too;
 *   - a candidate the gallery doesn't hold is added while slots remain, highest
 *     priority first;
 *   - an existing image the extractor no longer emits is NEVER removed. Losing
 *     curated content to an upstream page edit is the worse failure.
 *
 * Matching is by `sourceUrl`, which is why the importer stamps it. A gallery
 * image uploaded by hand has none and therefore matches nothing: its tags are
 * safe, but the same shot may be re-added from the source if a slot is free.
 */
export function planGalleryMerge(
  existing: readonly ImageInfo[] | undefined,
  candidates: readonly GalleryImageCandidate[],
  max: number = MAX_GALLERY_IMAGES,
): GalleryMergePlan {
  const held = existing ?? [];
  const indexByKey = new Map<string, number>();
  held.forEach((img, i) => {
    const source = String(img?.sourceUrl ?? '').trim();
    if (!source) return;
    const key = dedupKey(source);
    if (!indexByKey.has(key)) indexByKey.set(key, i);
  });

  const tagUpdates: GalleryTagUpdate[] = [];
  const toAdd: GalleryImageCandidate[] = [];
  const freeSlots = Math.max(0, max - held.length);

  for (const candidate of candidates) {
    const heldIndex = indexByKey.get(dedupKey(candidate.url));
    if (heldIndex !== undefined) {
      const current = held[heldIndex]?.tags ?? [];
      if (current.length === 0 && candidate.tags.length > 0) {
        tagUpdates.push({ index: heldIndex, tags: candidate.tags });
      }
      continue;
    }
    if (toAdd.length < freeSlots) toAdd.push(candidate);
  }

  return { tagUpdates, toAdd };
}

/** One entry in a rebuilt gallery: either bytes to fetch, or a slot to keep. */
export type GalleryReplaceEntry =
  /** A shot from the new scrape — the importer fetches and uploads it. */
  | {
      kind: 'upload';
      url: string;
      tags: GalleryImageTag[];
      /**
       * The held image this candidate replaces, when the reference already had
       * it by `sourceUrl`. Present so a FAILED fetch degrades to "kept what we
       * had" instead of dropping an asset we already own — these images come
       * from hosts that block hotlinking at random (Akamai/VCA), and a transient
       * 403 must not cost the curator bytes already in Storage.
       */
      fallback?: ImageInfo;
    }
  /** An image the reference already holds, kept as-is. No bytes move. */
  | { kind: 'keep'; image: ImageInfo };

/**
 * What a re-import should do to a reference's gallery when the admin ticked
 * "Replace all images" — the deliberate inversion of {@link planGalleryMerge}.
 *
 * The merge planner exists to protect what a reference already holds: it adds
 * into free slots and never removes. That is right for a routine re-import, and
 * wrong for the case this planner serves — an extractor whose image extraction
 * has since improved, where the images already stored are precisely the ones the
 * admin wants gone. So a shot the new scrape no longer emits IS dropped here.
 *
 * Two things survive that rebuild anyway, because neither is something the new
 * scrape has an opinion about:
 *
 *   - a HAND-UPLOADED image, i.e. one with no `sourceUrl`. It matches nothing in
 *     the scrape by construction, so a literal replace would delete it — and
 *     unlike a scraped shot, nothing can bring it back. It keeps its slot; the
 *     scrape fills what's left. This is the one rule shared with the merge
 *     planner, for the one reason that outlives the mode.
 *   - a CURATOR'S TAGS on an image the scrape re-emits, matched by `sourceUrl`.
 *     The bytes are re-fetched, the human's description of them is not
 *     second-guessed. Only when the held image carries no tags does the scrape's
 *     own guess apply.
 *
 * Note the hero is NOT among the protected: its tags come from the fresh scrape
 * every time, which is what the checkbox's helper text promises.
 *
 * Returns entries in final gallery order, capped at `max`. An empty result means
 * the scrape offered nothing — the caller must no-op rather than write an empty
 * array, or a barren re-scrape would wipe a good gallery.
 */
export function planGalleryReplace(
  existing: readonly ImageInfo[] | undefined,
  candidates: readonly GalleryImageCandidate[],
  max: number = MAX_GALLERY_IMAGES,
): GalleryReplaceEntry[] {
  const held = existing ?? [];

  // Held images the scrape can speak for, keyed by source. First occurrence
  // wins, matching planGalleryMerge — a duplicated sourceUrl is a data bug, not
  // a reason for the two planners to disagree about which entry is canonical.
  const heldByKey = new Map<string, ImageInfo>();
  const keepers: ImageInfo[] = [];
  for (const img of held) {
    const source = String(img?.sourceUrl ?? '').trim();
    if (!source) {
      keepers.push(img);
      continue;
    }
    const key = dedupKey(source);
    if (!heldByKey.has(key)) heldByKey.set(key, img);
  }

  // Hand-uploaded images take their slots first: they are the entries that
  // cannot be re-fetched, so they must not be the ones the cap squeezes out.
  const out: GalleryReplaceEntry[] = keepers
    .slice(0, max)
    .map((image) => ({ kind: 'keep' as const, image }));

  for (const candidate of candidates) {
    if (out.length >= max) break;
    const heldMatch = heldByKey.get(dedupKey(candidate.url));
    // Sanitise BEFORE deciding whether the curator has an opinion: a legacy doc
    // can hold a tag the vocabulary has since dropped, and treating that as "the
    // curator tagged this" would blank the image rather than fall through to the
    // extractor's guess.
    const curated = sanitizeImageTags(heldMatch?.tags);
    out.push({
      kind: 'upload',
      url: candidate.url,
      // A curator's tags beat the extractor's, exactly as in merge mode.
      tags: curated.length > 0 ? curated : candidate.tags,
      ...(heldMatch ? { fallback: heldMatch } : {}),
    });
  }

  return out;
}
